import { hydrateChatSummaryFromDb } from './hangout-settings-persist.js';
import { storage } from '../../utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import { withGracefulFailure } from '../../utils/error-handling.js';
import { initializeResumedVisuals } from './session-manager.js';
import { setDeferChatAvatarVideoUpgrade, upgradeDeferredChatAvatarsToVideo } from './chatbot-display-manager.js';
import {
    resolvePreferredLocalMessages,
    shouldFetchConversationFromBackend
} from '../shared/history-resolution.js';

const restoreConversationState = async (hangoutState, resumeConversationId, config, currentSession, existingMessages) => {
    const characterId = config?.allCharacters?.[0]?.id || currentSession?.characterId;
    const scenarioId = config?.scenarioId || currentSession?.scenarioId;

    if (!characterId) {
        console.warn('restoreConversationState: No character ID found for resume, loading messages directly');
        hangoutState.setMessages(existingMessages);
        hangoutState.loadActiveVersionsForConversation(resumeConversationId);
        return existingMessages?.length > 0;
    }

    const resumeSuccess = await withGracefulFailure(async () => {
        return await hangoutState.resumeConversation(
            resumeConversationId,
            characterId,
            scenarioId
        );
    }, 'Resume conversation state');

    if (resumeSuccess) {
        await withGracefulFailure(
            () => hydrateChatSummaryFromDb(resumeConversationId),
            'Hydrate hangout chat summary'
        );
        return true;
    }

    console.warn('restoreConversationState: Resume failed, loading messages directly');
    if (existingMessages?.length > 0) {
        hangoutState.setMessages(existingMessages);
        hangoutState.loadActiveVersionsForConversation(resumeConversationId);
        return true;
    }
    return false;
};

const handleDelayedVisualRestoration = async () => {
    setTimeout(async () => {
        await initializeResumedVisuals();
        await new Promise(resolve => setTimeout(resolve, 50));

        await withGracefulFailure(async () => {
            const { getCurrentLayoutMode, LAYOUT_MODES } = await import('./layout-mode.js');
            if (getCurrentLayoutMode() !== LAYOUT_MODES.VN) return;
            const { getDisplayManager } = await import('./message-utils.js');
            const displayManager = await getDisplayManager();
            const displayedCharacterId = typeof displayManager?.getDisplayedCharacterId === 'function'
                ? displayManager.getDisplayedCharacterId()
                : null;
            if (!displayedCharacterId) return;
            displayManager.syncMobilePortraitCharacterFocus?.(displayedCharacterId);
        }, 'Re-sync VN character focus after resumed visuals');

        await withGracefulFailure(
            () => upgradeDeferredChatAvatarsToVideo(),
            'Upgrade chatbot avatars to video after resumed visuals'
        );
        setDeferChatAvatarVideoUpgrade(false);
    }, 150);
};

const safeClearBubbles = async () => {
    const { getMessageDisplayManager } = await import('./message-display-manager.js');
    const displayManager = await getMessageDisplayManager();
    await displayManager.clearActiveMessages();
};

const finalizeResumeUI = async (
    hangoutState,
    config,
    initializeUI,
    setupEventListeners,
    setupScenarioDetails,
    options = {}
) => {
    const {
        initializeVisuals = true,
        skipScenarioSetup = false
    } = options;

    await hangoutState.initIndexedDB();
    setDeferChatAvatarVideoUpgrade(true);
    try {
        await initializeUI();
        setupEventListeners();

        if (!skipScenarioSetup && config?.scenarioId) {
            await setupScenarioDetails(hangoutState, config.scenarioId);
        }

        if (initializeVisuals) {
            await initializeResumedVisuals();
        }

        await withGracefulFailure(async () => {
            const { syncChatbotVisibleHistory } = await import('./message-utils.js');
            await syncChatbotVisibleHistory();
        }, 'Sync chatbot history after resume');

        if (initializeVisuals) {
            await withGracefulFailure(
                () => upgradeDeferredChatAvatarsToVideo(),
                'Upgrade chatbot avatars to video after resume'
            );
            setDeferChatAvatarVideoUpgrade(false);
        }
    } catch (error) {
        setDeferChatAvatarVideoUpgrade(false);
        throw error;
    }
};

export const checkAndSendWelcomeBackMessage = async () => {
    const shouldSendWelcomeBack = storage.get('sendWelcomeBackMessage');
    if (shouldSendWelcomeBack !== 'true') return;

    storage.remove('sendWelcomeBackMessage');

    const pendingMessage = storage.get('pendingMessageToSend');
    if (!pendingMessage) return;

    storage.remove('pendingMessageToSend');
    const messageToSend = pendingMessage;

    await new Promise(resolve => setTimeout(resolve, 1500));
    window._welcomeBackInProgress = true;

    try {
        const { sendMessage } = await import('./message-utils.js');
        await sendMessage(messageToSend);
    } catch (error) {
        console.error('[Hangout] Error sending welcome back message:', error);
        window._welcomeBackInProgress = false;
    }
};

const preloadConversationData = async (resumeConversationId, config) => {
    if (!resumeConversationId || config?.isNewConversation) return;
    if (!shouldFetchConversationFromBackend(resumeConversationId)) return;
    await withGracefulFailure(() => storage.getCompleteConversation(resumeConversationId), 'Preload conversation data');
};

const shouldHandlePageRefreshResume = (resumeConversationId, config = null) => {
    return Boolean(
        resumeConversationId &&
        resumeConversationId !== 'undefined' &&
        !resumeConversationId.startsWith('conv-') &&
        config?.entrypoint === 'hangout_page_refresh'
    );
};

export const restoreThenRender = async ({
    hangoutState,
    conversationId,
    config,
    currentSession,
    initializeUI,
    setupEventListeners,
    setupScenarioDetails,
    source
}) => {
    const existingMessages = resolvePreferredLocalMessages(conversationId).messages;
    const delayVisuals = source === 'refresh';
    const skipScenarioSetup = source === 'explicit' && !!config?.scenarioId;

    if (source === 'refresh') {
        if (existingMessages.length === 0 && config?.isNewConversation) return false;
        try {
            const { fetchCache } = await import('../../utils/fetchCache.js');
            await fetchCache.preloadCommonResources();
        } catch (error) {
            console.warn('restoreThenRender: Failed to preload resources:', error);
        }
        await safeClearBubbles();
    }

    if (source === 'rankings' && existingMessages.length === 0) return false;

    if (source === 'explicit') {
        if (config?.isNewConversation || !conversationId) return false;

        if (config.scenarioId) {
            await setupScenarioDetails(hangoutState, config.scenarioId);
        }

        if (conversationId && config.allCharacters?.[0]?.id && config.scenarioId) {
            const restored = await restoreConversationState(
                hangoutState,
                conversationId,
                config,
                currentSession,
                existingMessages
            );
            if (restored) {
                await finalizeResumeUI(
                    hangoutState,
                    config,
                    initializeUI,
                    setupEventListeners,
                    setupScenarioDetails,
                    { skipScenarioSetup: true, initializeVisuals: !delayVisuals }
                );
                return true;
            }
        }

        if (conversationId && (!config.allCharacters?.length || !config.scenarioId)) {
            try {
                if (existingMessages.length > 0) {
                    const updatedConfig = await storageManager.getItem('currentHangoutConfig');
                    if (updatedConfig?.allCharacters?.length && updatedConfig.scenarioId) {
                        const retried = await restoreConversationState(
                            hangoutState,
                            conversationId,
                            updatedConfig,
                            currentSession,
                            existingMessages
                        );
                        if (retried) {
                            await finalizeResumeUI(
                                hangoutState,
                                updatedConfig,
                                initializeUI,
                                setupEventListeners,
                                setupScenarioDetails
                            );
                            return true;
                        }
                    }

                    hangoutState.setMessages(existingMessages);
                    hangoutState.loadActiveVersionsForConversation(conversationId);
                    await finalizeResumeUI(
                        hangoutState,
                        config,
                        initializeUI,
                        setupEventListeners,
                        setupScenarioDetails
                    );
                    return true;
                }
            } catch (error) {
                console.warn('restoreThenRender: Failed to recover data from local history:', error);
            }
        }

        if (currentSession && currentSession.conversationId === config.conversationId) {
            await finalizeResumeUI(
                hangoutState,
                config,
                initializeUI,
                setupEventListeners,
                setupScenarioDetails,
                { skipScenarioSetup: true }
            );
            return true;
        }

        console.warn('restoreThenRender: Resume failed for conversation:', conversationId);
        return false;
    }

    const restored = await restoreConversationState(
        hangoutState,
        conversationId,
        config,
        currentSession,
        existingMessages
    );
    if (!restored && existingMessages.length === 0) return false;

    await finalizeResumeUI(
        hangoutState,
        config,
        initializeUI,
        setupEventListeners,
        setupScenarioDetails,
        { initializeVisuals: !delayVisuals, skipScenarioSetup }
    );

    if (delayVisuals) {
        await handleDelayedVisualRestoration();
    }

    return true;
};

export const dispatchResume = async (
    hangoutState,
    currentSession,
    config,
    initializeUI,
    setupEventListeners,
    setupScenarioDetails
) => {
    const conversationId = currentSession?.conversationId || hangoutState.getConversationId() || config?.conversationId;
    const shared = {
        hangoutState,
        currentSession,
        config,
        initializeUI,
        setupEventListeners,
        setupScenarioDetails
    };

    if (config?.conversationId && !config?.isNewConversation && config?.scenarioId) {
        const handled = await restoreThenRender({
            ...shared,
            conversationId: config.conversationId,
            source: 'explicit'
        });
        if (handled) return true;
    }

    if (shouldHandlePageRefreshResume(conversationId, config)) {
        const handled = await restoreThenRender({
            ...shared,
            conversationId,
            source: 'refresh'
        });
        if (handled) return true;
    }

    await preloadConversationData(conversationId, config);

    if (config?.isNewConversation) return false;

    const { cleanupAllAudioCache } = await import('./message-utils.js');
    cleanupAllAudioCache();

    if (conversationId && conversationId !== 'undefined') {
        const { messages } = resolvePreferredLocalMessages(conversationId);
        if (messages.length > 0) {
            const handled = await restoreThenRender({
                ...shared,
                conversationId,
                source: 'rankings'
            });
            if (handled) return true;
        }
    }

    if (config?.conversationId) {
        return restoreThenRender({
            ...shared,
            conversationId: config.conversationId,
            source: 'explicit'
        });
    }

    return false;
};
